/** Ресурсы, доступы к аккаунтам и финансы. */

import type { AccountAccessDto, FinanceDto, ResourcesDto } from '@project-os/contracts';
import type { GrantAccountAccessInput } from '@project-os/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './client';
import { itemKeys } from './items';
import { stageKeys } from './stages';

export const resourceKeys = {
  resources: (projectId: string) => ['projects', projectId, 'resources'] as const,
  finance: (projectId: string, filter: FinanceQuery) =>
    ['projects', projectId, 'finance', filter] as const,
  access: (projectId: string, itemId: string) =>
    ['projects', projectId, 'items', itemId, 'access'] as const,
};

export interface FinanceQuery {
  currency?: string;
  category?: string;
  includeCancelled?: boolean;
}

export function useResources(projectId: string | undefined) {
  return useQuery({
    queryKey: resourceKeys.resources(projectId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<ResourcesDto>(`/projects/${projectId}/resources`, { signal }),
    enabled: Boolean(projectId),
  });
}

export function useFinance(projectId: string | undefined, filter: FinanceQuery) {
  const params = new URLSearchParams();
  if (filter.currency) params.set('currency', filter.currency);
  if (filter.category) params.set('category', filter.category);
  if (filter.includeCancelled) params.set('includeCancelled', 'true');
  const search = params.toString();

  return useQuery({
    queryKey: resourceKeys.finance(projectId ?? '', filter),
    queryFn: ({ signal }) =>
      apiRequest<FinanceDto>(`/projects/${projectId}/finance${search ? `?${search}` : ''}`, {
        signal,
      }),
    enabled: Boolean(projectId),
  });
}

export function useAccountAccess(projectId: string, itemId: string | null) {
  return useQuery({
    queryKey: resourceKeys.access(projectId, itemId ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<AccountAccessDto[]>(`/projects/${projectId}/items/${itemId}/access`, { signal }),
    enabled: Boolean(itemId),
  });
}

/** Изменение доступа обновляет и карточку аккаунта, и раздел ресурсов. */
function useAccessMutation<TInput>(
  projectId: string,
  itemId: string,
  request: (input: TInput) => Promise<AccountAccessDto[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (access) => {
      queryClient.setQueryData(resourceKeys.access(projectId, itemId), access);
      void queryClient.invalidateQueries({ queryKey: resourceKeys.resources(projectId) });
      void queryClient.invalidateQueries({ queryKey: itemKeys.detail(projectId, itemId) });
      void queryClient.invalidateQueries({ queryKey: stageKeys.dashboard(projectId) });
    },
  });
}

export function useGrantAccess(projectId: string, itemId: string) {
  return useAccessMutation(projectId, itemId, (input: GrantAccountAccessInput) =>
    apiRequest<AccountAccessDto[]>(`/projects/${projectId}/items/${itemId}/access`, {
      method: 'POST',
      body: input,
    }),
  );
}

export function useRevokeAccess(projectId: string, itemId: string) {
  return useAccessMutation(projectId, itemId, (accessId: string) =>
    apiRequest<AccountAccessDto[]>(`/projects/${projectId}/access/${accessId}`, {
      method: 'DELETE',
    }),
  );
}
